import { Component, inject, Input, OnInit } from '@angular/core';
import { EpTableComponent, EPTableConfig } from '@ep/shared';
import { BatchTableDisplay } from '../../models/BatchTableDisplay';
import { AsyncPipe } from '@angular/common';
import { ReportsApiService } from '../../services';
import { map, Observable, of, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { Batch } from '@app/resources/services';
import { PortalTheme } from '@app/@shared/models';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { BreadcrumbService } from '@app/resources/services/breadcrumb.service';
import { FA_ICON } from '@shared/icons';

@Component({
  selector: 'app-existing-batch-report',
  standalone: true,
  imports: [EpTableComponent, AsyncPipe],
  template: `
    <div class="p-4">
      <app-ep-table
        [config]="config"
        [data]="batches$ | async"
        data-cy="batches-table"
        (dateChange)="dateChange($event)"
      />
    </div>
  `,
  styleUrl: './existing-batch-report.component.scss',
})
export class ExistingBatchReportComponent implements OnInit {
  private readonly reportService = inject(ReportsApiService);
  private readonly router = inject(Router);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  protected calendarFilter: Date | undefined;

  @Input() public theme: PortalTheme | null = null;
  protected config!: EPTableConfig<BatchTableDisplay>;

  private initializeTableConfig(): void {
    this.config = {
      header: 'Batches',
      pagination: {
        rows: 10,
        showCurrentPageReport: true,
      },
      columns: [
        {
          header: 'Batch Id',
          field: 'MerchantBatchId',
          columnType: 'string',
          defaultValue: null,
        },
        {
          header: 'Batch Closed Date',
          field: 'ClosedDateTime',
          columnType: 'date',
          dateFormat: 'MM/dd/YY h:mm a',
          defaultValue: null,
        },
      ],
      style: {
        'custom-header-background': this.theme?.table.header.backgroundColor || '',
        'custom-table-subheader-background': this.theme?.table.subHeaderBackgroundColor || '',
        'custom-table-subheader-text-color': this.theme?.table.subHeaderTextColor || '',
        'custom-header-text-color': this.theme?.table.header.textColor || '',
        'custom-table-body-primary-background': this.theme?.table.body.primaryBackgroundColor || '',
        'custom-table-body-secondary-background': this.theme?.table.body.secondaryBackgroundColor || '',
        'custom-table-body-text-color': this.theme?.table.body.textColor || '',
      },
      calendarFilter: true,
      calendarDate: this.router.parseUrl(this.router.url).queryParams['date']
        ? new Date(`${this.router.parseUrl(this.router.url).queryParams['date'] + 'T00:00:00'}`)
        : new Date(),
      actions: {
        rowClick: (batch: BatchTableDisplay) => {
          this.navigateToBatchDetail(batch.BatchId, batch.ClosedDateTime);
        },
      },
    };
  }

  protected batches$: Observable<Batch[]> = of();
  protected location$: Observable<number> = this.applicationProfileService.applicationProfile$.pipe(
    switchMap((appProfile) => {
      return of(appProfile.Location.LocationId);
    })
  );

  public ngOnInit(): void {
    this.breadcrumbService.setPageBreadcrumbs([
      { label: 'Reports', icon: FA_ICON.FILE_EXPORT, command: () => this.navigateBack() },
      { label: 'Batches' },
    ]);

    const queryParams = this.router.parseUrl(this.router.url).queryParams;

    if (queryParams['date']) {
      this.batches$ = this.reportService
        .searchForBatchesByDate(new Date(`${queryParams['date']}T00:00:00`))
        .pipe(map((batches) => batches));
    } else {
      this.batches$ = this.reportService.searchForBatchesByDate(new Date()).pipe(map((batches) => batches));
    }

    this.handleSingleBatchNavigation();
    this.initializeTableConfig();
    this.setCSSCustomProperties();
  }

  private handleSingleBatchNavigation(): void {
    const queryParams = this.router.parseUrl(this.router.url).queryParams;
    if (queryParams['date']) {
      return;
    }

    this.batches$.subscribe((batches) => {
      if (batches.length === 1) {
        this.navigateToBatchDetail(batches[0].BatchId, batches[0].ClosedDateTime);
      }
    });
  }

  private navigateToBatchDetail(batchId: number, closedDateTime?: Date | string | null): void {
    this.location$.subscribe((location) => {
      this.router.navigate([`${location}/batch-detail`, batchId], {
        queryParams: { date: closedDateTime?.toString().split('T')[0] },
      });
    });
  }

  public dateChange(date: Date): void {
    this.batches$ = this.reportService.searchForBatchesByDate(date).pipe(map((batches) => batches));

    // Clear the queryParams to allow the Batch Report detail to be loaded
    this.router.navigate([], {
      queryParams: { date: null },
      queryParamsHandling: 'merge',
    });

    this.calendarFilter = date;

    setTimeout(() => {
      this.handleSingleBatchNavigation();
    }, 100);
  }

  public navigateBack(): void {
    this.location$.subscribe((location) => {
      this.router.navigate([`${location}/reports`]);
    });
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      // Set CSS custom properties
      document.documentElement.style.setProperty(
        '--custom-header-background',
        this.theme?.table.header.backgroundColor
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-background',
        this.theme?.table.subHeaderBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-text-color',
        this.theme?.table.subHeaderTextColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-header-text-color',
        this.theme?.table.header.textColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-primary-background',
        this.theme?.table.body.primaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-secondary-background',
        this.theme?.table.body.secondaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-text-color',
        this.theme?.table.body.textColor || ''
      );

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('ExistingBatchReportComponent');
    }
  }
}
